import { useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const InstallPrompt = () => {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [showManualInstructions, setShowManualInstructions] = useState(false);

  useEffect(() => {
    // Check if the user has dismissed the prompt before
    const hasBeenDismissed = localStorage.getItem('installPromptDismissed') === 'true';
    if (hasBeenDismissed) {
      setIsDismissed(true);
      return;
    }

    // Detect iOS and Android
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    const isAndroidDevice = /Android/.test(navigator.userAgent);
    setIsIOS(isIOSDevice);
    setIsAndroid(isAndroidDevice);
    
    // Check if the app is already installed
    const isStandaloneMode = window.matchMedia('(display-mode: standalone)').matches || 
                            (window.navigator as any).standalone === true;
    setIsStandalone(isStandaloneMode);
    
    if (isStandaloneMode) {
      // App is already installed, don't show prompt
      return;
    } else if (isIOSDevice || isAndroidDevice) {
      // Show device-specific instructions
      setShowPrompt(true);
    }

    // For non-iOS devices, listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent Chrome 67 and earlier from automatically showing the prompt
      e.preventDefault();
      // Stash the event so it can be triggered later
      setInstallPrompt(e as BeforeInstallPromptEvent);
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = () => {
    if (!installPrompt) {
      // If no install prompt is available but we're on Android,
      // show manual instructions
      if (isAndroid) {
        setShowManualInstructions(true);
      }
      return;
    }

    // Show the install prompt
    installPrompt.prompt();

    // Wait for the user to respond to the prompt
    installPrompt.userChoice.then((choiceResult) => {
      if (choiceResult.outcome === 'accepted') {
        console.log('User accepted the install prompt');
      } else {
        console.log('User dismissed the install prompt');
        // If user dismissed the prompt but we're on Android,
        // show manual instructions
        if (isAndroid) {
          setShowManualInstructions(true);
        }
      }
      setInstallPrompt(null);
      setShowPrompt(false);
    });
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    localStorage.setItem('installPromptDismissed', 'true');
  };

  // Don't show if already installed, already dismissed, or no prompt available
  if (isStandalone || isDismissed || (!showPrompt && !isIOS)) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 bg-[var(--dash-surface)] text-[color:var(--dash-ink)] border border-[color:var(--dash-hairline)] p-4 rounded-lg shadow-[var(--dash-shadow)] z-50 max-w-md mx-auto">
      <div className="flex justify-between items-start mb-2">
        <h3 className="font-semibold text-lg">Install this app</h3>
        <button
          onClick={handleDismiss}
          className="text-[color:var(--dash-muted)] hover:text-[color:var(--dash-ink)] transition-colors"
          aria-label="Dismiss"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
      
      {isIOS ? (
        <div>
          <p className="mb-2">Install this app on your iPhone:</p>
          <ol className="list-decimal pl-5 mb-2 text-sm">
            <li>Tap the Share button <span className="inline-block w-5 h-5 bg-[var(--dash-surface-2)] rounded-full flex items-center justify-center">↑</span> at the bottom of your browser</li>
            <li>Scroll down and tap "Add to Home Screen"</li>
            <li>Tap "Add" in the top right corner</li>
          </ol>
        </div>
      ) : isAndroid && showManualInstructions ? (
        <div>
          <p className="mb-2">Install this app on your Android device:</p>
          <ol className="list-decimal pl-5 mb-2 text-sm">
            <li>Tap the menu button <span className="inline-block w-5 h-5 bg-[var(--dash-surface-2)] rounded-full flex items-center justify-center">⋮</span> in the top right of your browser</li>
            <li>Tap "Install app" or "Add to Home screen"</li>
            <li>Follow the on-screen instructions to complete installation</li>
          </ol>
        </div>
      ) : (
        <>
          <p className="mb-2">Install this app on your device for quick and easy access!</p>
          <button
            onClick={handleInstallClick}
            className="cursor-pointer bg-[var(--dash-accent)] text-[color:var(--dash-band-active-ink,var(--structural-white))] px-4 py-2 rounded-[9px] text-[12px] font-[600] transition-[transform,filter] duration-150 ease-[var(--dash-ease)] hover:brightness-95 active:scale-[.97] motion-reduce:transition-none motion-reduce:active:scale-100"
          >
            Install
          </button>
        </>
      )}
    </div>
  );
};

export default InstallPrompt;
